import { useQuery } from '@apollo/client/react'
import { GET_CATEGORIES } from 'api/symfony/graphql/queries/getCategories'
import React from 'react'

import { parseCategoriesResponse } from 'api/symfony/parsers/graphqlParsers'

import { Category, Query } from '@definitions/types/symfonyTypes'

export interface GroupedCategory {
  parent: Category
  children: Category[]
}

export const useCategories = (
  service: boolean,
  affiliationNames: string[] = []
): {
  loading: boolean
  error: Error | undefined
  data: Category[]
  groupedData: GroupedCategory[]
} => {
  const { loading, error, data } = useQuery<Query>(GET_CATEGORIES, {
    variables: { service },
  })

  const categoriesData = React.useMemo(() => {
    return parseCategoriesResponse(data?.categories ?? null)
  }, [data])

  const filteredData = React.useMemo(() => {
    const filteredCategories = categoriesData.filter((node: Category) => {
      const affiliationName = node.affiliation?.name
      return affiliationName
        ? affiliationNames.includes(affiliationName)
        : false
    })

    const groupedCategories = filteredCategories.reduce(
      (acc: Record<string, GroupedCategory>, node: Category) => {
        if (!node || !node.parent) return acc

        const parentName = node.parent.name
        if (!acc[parentName]) {
          acc[parentName] = { parent: node.parent, children: [] }
        }
        acc[parentName].children.push(node)
        return acc
      },
      {} as Record<string, GroupedCategory>
    )

    return Object.values(groupedCategories) as GroupedCategory[]
  }, [categoriesData, affiliationNames])

  return {
    loading,
    error,
    data: categoriesData,
    groupedData: filteredData,
  }
}
